import { createLivePreviewRequestHandler } from "src/utils/livePreviewHandler";

const fetchRestaurantPromoDoc = createLivePreviewRequestHandler(
  ({ serverURL, apiPath, docId }) =>
    `${serverURL}${apiPath}/restaurant-promotions/${docId}?draft=true&depth=2`,
);

/**
 * @param {unknown} ref
 * @param {string} serverURL
 * @param {string} apiPath
 */
async function hydrateMediaRef(ref, serverURL, apiPath) {
  if (
    ref &&
    typeof ref === "object" &&
    !Array.isArray(ref) &&
    (typeof ref.url === "string" || typeof ref.filename === "string")
  ) {
    return ref;
  }

  if (typeof ref === "number" || (typeof ref === "string" && /^\d+$/.test(ref))) {
    try {
      const mediaResponse = await fetch(
        `${serverURL}${apiPath}/media/${ref}?depth=0`,
      );
      if (mediaResponse.ok) return mediaResponse.json();
    } catch {
      // Keep original ref if hydration fails.
    }
  }

  return ref;
}

/**
 * @param {unknown} value
 * @param {string} serverURL
 * @param {string} apiPath
 */
async function hydrateLocalizedMedia(value, serverURL, apiPath) {
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("en" in value || "fr" in value) &&
    !value.url &&
    !value.filename
  ) {
    const out = { ...value };
    for (const key of ["en", "fr"]) {
      if (key in out) {
        out[key] = await hydrateMediaRef(out[key], serverURL, apiPath);
      }
    }
    return out;
  }

  return hydrateMediaRef(value, serverURL, apiPath);
}

/**
 * @param {unknown[]} blocks
 * @param {string} serverURL
 * @param {string} apiPath
 */
async function hydrateSectionMedia(blocks, serverURL, apiPath) {
  if (!Array.isArray(blocks)) return;
  for (const block of blocks) {
    if (!block || typeof block !== "object") continue;

    for (const key of ["bgImage", "image", "src"]) {
      if (block[key] != null) {
        block[key] = await hydrateLocalizedMedia(block[key], serverURL, apiPath);
      }
    }

    if (block.blockType === "diningPromoSplitSection") {
      await hydrateSectionMedia(block.leftSections, serverURL, apiPath);
      await hydrateSectionMedia(block.rightSections, serverURL, apiPath);
    }

    if (block.blockType === "diningPromoStackSection") {
      await hydrateSectionMedia(block.sections, serverURL, apiPath);
    }
  }
}

/**
 * Live preview request handler for `restaurant-promotions`.
 *
 * @param {import('@payloadcms/live-preview').CollectionPopulationRequestHandlerArgs} args
 */
export async function restaurantPromoLivePreviewRequestHandler(args) {
  const { apiPath, serverURL } = args;
  const response = await fetchRestaurantPromoDoc(args);

  if (!response.ok) return response;

  let doc;
  try {
    doc = await response.json();
  } catch {
    return response;
  }

  if (doc && typeof doc === "object") {
    await hydrateSectionMedia(doc.sections, serverURL, apiPath);
  }

  return new Response(JSON.stringify(doc), {
    status: 200,
    headers: { "Content-Type": "application/json" },
  });
}
